<template>
  <div class="w-full px-6 py-4">
    <div>
      <div class="md:flex space-x-4 mb-6">
        <o-field-group
          v-if="_codeField === 'single'"
          v-slot="{ status }"
          rules="required|alpha_num"
          :validation-value="_voucher.code"
          class="w-full"
          :label="$t('general.code')"
          :name="$t('general.code')"
          :info="$t('components.voucher.edit.details.info')"
        >
          <text-field v-model="_voucher.code" :label="$t('general.code')" :status="status" />

          <p v-if="!isEditMode" class="text-right mt-1">
            <a
              class="text-blue-500 text-sm cursor-pointer hover:underline text-right"
              @click="_codeField = 'multiple'"
            >
              {{ $t('components.voucher.edit.details.createMultiple') }}
            </a>
          </p>
        </o-field-group>

        <o-field-group
          v-if="_codeField === 'multiple'"
          v-slot="{ status }"
          rules="required"
          :validation-value="_codeList"
          class="w-full"
          :label="$t('components.voucher.edit.details.codeList')"
          :name="$t('components.voucher.edit.details.codeList')"
          :info="$t('components.voucher.edit.details.codeListInfo')"
        >
          <list-field
            v-model="_codeList"
            :status="status"
            @change="handleCodeListChange"
          />

          <p v-if="!isEditMode" class="text-right mt-1">
            <a
              class="text-blue-500 text-sm cursor-pointer hover:underline text-right"
              @click="_codeField = 'single'"
            >
              {{ $t('components.voucher.edit.details.createSingle') }}
            </a>
          </p>
        </o-field-group>
      </div>
    </div>

    <div class="md:flex space-x-4 mb-6">
      <o-field-group
        v-slot="{ status }"
        rules="required"
        :validation-value="_voucher.name"
        class="w-full"
        :label="$t('general.name')"
        :name="$t('general.name')"
        :info="$t('components.voucher.edit.details.voucherTitle')"
      >
        <text-field v-model="_voucher.name" :label="$t('general.name')" :status="status" />
      </o-field-group>
    </div>

    <div class="flex space-x-4 mb-6">
      <o-field-group
        v-slot="{ status }"
        rules="required"
        :validation-value="_voucher.information"
        class="w-full"
        :label="$t('general.info')"
        :info="$t('components.voucher.edit.details.infoInfo')"
      >
        <text-area-field
          v-model="_voucher.information"
          rows="3"
          :status="status"
        />
      </o-field-group>
    </div>

    <o-field-group
      v-slot="{ status }"
      :label="$t('components.voucher.edit.details.discountAmount')"
      :name="$t('components.voucher.edit.details.discountAmount')"
      class="mb-8 block"
      :rules="isDiscountTypePercentage ? 'between:0,100' : 'currency'"
      :validation-value="_voucher.discountAmount"
    >
      <div class="flex items-center space-x-2">
        <currency-field
          v-if="isDiscountTypeFixed"
          v-model="_voucher.discountAmount"
          :status="status"
          class="w-1/2"
        />
        <number-field
          v-if="isDiscountTypePercentage"
          v-model="_voucher.discountAmount"
          :min="0"
          :max="100"
          slim
          :status="status"
          trailing="%"
          class="w-1/2"
        />
        <segment-field
          v-model="_voucher.discountType"
          :options="voucherDiscountTypeOptions"
          class="border w-1/2"
        />
      </div>
    </o-field-group>

    <div class="md:flex space-x-4 mb-6 items-end">
      <o-field-group
        class="w-full"
        :label="$t('components.voucher.edit.details.customerType')"
        :info="$t('components.voucher.edit.details.customerTypeInfo')"
      >
        <select-field
          v-model="_voucher.customerType"
          :options="voucherCustomerTypeOptions"
        />
      </o-field-group>
    </div>

    <div v-if="isCustomerTypeSegments" class="md:flex space-x-4 mb-6 items-end">
      <o-field-group class="w-full" :label="$t('general.segments')">
        <segment-select v-model="segmentValues" />
      </o-field-group>
    </div>

    <div class="md:flex space-x-4 mb-6 items-end">
      <o-field-group
        class="w-full"
        :label="$t('components.voucher.edit.details.multipleServiceMode')"
        :info="$t('components.voucher.edit.details.multipleServiceModeInfo')"
      >
        <select-field
          v-model="_voucher.multipleMode"
          :options="voucherMultipleModeOptions"
        />
      </o-field-group>
    </div>

    <div class="md:flex space-x-4 mb-6">
      <o-field-group
        v-slot="{ status }"
        :message="$t('components.voucher.edit.details.leaveBlankNoLimit')"
        class="w-full md:w-1/2"
        :label="$t('components.voucher.edit.details.appointmentLimit')"
        :info="$t('components.voucher.edit.details.appointmentLimitInfo')"
      >
        <number-field v-model="_voucher.reservationLimit" :status="status" />
      </o-field-group>

      <o-field-group
        v-slot="{ status }"
        :message="$t('components.voucher.edit.details.leaveBlankNoLimit')"
        class="w-full md:w-1/2"
        :label="$t('components.voucher.edit.details.appointmentLimitPerCustomer')"
        :info="$t('components.voucher.edit.details.appointmentLimitPerCustomerInfo')"
      >
        <number-field
          v-model="_voucher.reservationLimitPerCustomer"
          :status="status"
        />
      </o-field-group>
    </div>

    <div class="md:flex space-x-4 mb-6">
      <o-field-group
        v-slot="{ status }"
        :message="$t('components.voucher.edit.details.leaveBlankMinSpend')"
        class="w-full md:w-1/2"
        :label="$t('components.voucher.edit.details.minimumSpend')"
        :info="$t('components.voucher.edit.details.minimumSpendInfo')"
      >
        <currency-field v-model="_voucher.minimumSpend" :status="status" />
      </o-field-group>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { useSegmentStore } from '~/stores/useSegmentStore'


import SegmentSelect from '@/components/segment/elements/Select.vue'

import {
  TextField,
  CurrencyField,
  SelectField,
  NumberField,
  TextAreaField,
  SegmentField,
  DateField,
  ListField,
} from '@/components/fields'

import { augmenters as Augmenters } from '@/composables/useFieldAugmenters'

import {
  VoucherDiscountType,
  VoucherDiscountTypeLabels,
  VoucherTaxType,
  VoucherTaxTypeLabels,
  VoucherCustomerType,
  VoucherCustomerTypeLabels,
  VoucherMultipleMode,
  VoucherMultipleModeLabels,
} from '~/_api/models/voucher'

interface Props {
  voucher: Record<string, any>
  codeField?: string
  codeList?: any[]
  isEditMode?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  codeField: 'single',
  codeList: () => [],
  isEditMode: false,
})

const emit = defineEmits<{
  (e: 'update:voucher', value: Record<string, any>): void
  (e: 'update:codeField', value: string): void
  (e: 'update:codeList', value: any[]): void
}>()

const { t } = useI18n()
const { $notification } = useNuxtApp()
const segmentStore = useSegmentStore()

const segmentValues = ref<any[]>([])

const segments = computed(() => segmentStore.all)

const _voucher = computed({
  get () {
    return props.voucher
  },
  set (value) {
    emit('update:voucher', value)
  },
})

const _codeField = computed({
  get () {
    return props.codeField
  },
  set (value) {
    emit('update:codeField', value)
  },
})

const _codeList = computed({
  get () {
    return props.codeList
  },
  set (value) {
    emit('update:codeList', value)
  },
})

const isDiscountTypeFixed = computed(() => props.voucher.isDiscountTypeFixed)
const isDiscountTypePercentage = computed(() => props.voucher.isDiscountTypePercentage)
const isCustomerTypeSegments = computed(() => props.voucher.isCustomerTypeSegments)

const voucherDiscountTypeOptions = computed(() => {
  return Object.values(VoucherDiscountType)
    .filter((v): v is VoucherDiscountType => typeof v === 'number')
    .map((value) => ({
      label: t(VoucherDiscountTypeLabels[value]),
      value,
    }))
})

const voucherTaxTypeOptions = computed(() => {
  return Object.values(VoucherTaxType)
    .filter((v): v is VoucherTaxType => typeof v === 'number')
    .map((value) => ({
      label: t(VoucherTaxTypeLabels[value]),
      value,
    }))
})

const voucherCustomerTypeOptions = computed(() => {
  return Object.values(VoucherCustomerType)
    .filter((v): v is VoucherCustomerType => typeof v === 'number')
    .map((value) => ({
      label: t(VoucherCustomerTypeLabels[value]),
      value,
    }))
})

const voucherMultipleModeOptions = computed(() => {
  return Object.values(VoucherMultipleMode)
    .filter((v): v is VoucherMultipleMode => typeof v === 'number')
    .map((value) => ({
      label: t(VoucherMultipleModeLabels[value]),
      value,
    }))
})

watch(_voucher, () => {
  const segs =
    segments.value.filter((segment: any) => {
      return _voucher.value.segmentIDs?.includes(segment.idSegment)
    }) || []

  segmentValues.value = segs
}, { immediate: true })

watch(segmentValues, (value) => {
  _voucher.value.segmentIDs = value.map((segment: any) => segment.idSegment)
}, { deep: true })

watch(() => props.codeField, () => {
  _voucher.value.code = null
  _codeList.value = []
})

function handleCodeListChange () {
  if (new Set(_codeList.value).size !== _codeList.value.length) {
    $notification.error({ errors: [t('components.voucher.edit.details.codesUnique')] })
    _codeList.value = [...new Set(_codeList.value)]
  }

  if (_codeList.value.length > 100) {
    $notification.error({ errors: [t('components.voucher.edit.details.codeLimit')] })
    _codeList.value.splice(100)
  }
}
</script>
